'use client';

import { useDisclosure } from '@mantine/hooks';
import { Button, Modal, Textarea, Stack, Tooltip, CopyButton } from '@mantine/core';
import { useState } from 'react';
import styles from './SliceMachineWrapper.module.scss';
import { addDeploymentToast } from '@/src/helpers/add-deployment-tip';

export const ModalWithJsonPrompt = ({ jsonData, locale, pageKey }: { jsonData: any, locale: 'en' | 'fr', pageKey: string }) => {
    const [opened, { open, close }] = useDisclosure(false);
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [prompt, setPrompt] = useState('');
    const [editableJson, setEditableJson] = useState(JSON.stringify(jsonData, null, 2));

    const fullSliceSchema = [
        { type: 'homepage-banner', src: '...', alt: '...', title: '...', subtitle: '...', subtitle2: '...', name: '...' },
        { type: 'square-card-list', data: '...' },
        { type: 'horizontal-image-card', src: '...', backgroundGradient: '...', title: '...', description: '...', button: '...', buttonColor: '...', buttonHref: '...', position: '...' },
        { type: 'horizontal-text-card', version: '...', backgroundGradient: '...', title: '...', description: '...', button: '...', buttonColor: '...', buttonHref: '...', position: '...' },
        { type: 'grid-presentation', services: '...', contactInfo: '...' },
        { type: 'cards-carousel', title: '...', cardData: '...' },
        { type: 'text-card', title: '...', description: '...', button: '...', buttonColor: '...', buttonHref: '...' },
        { type: 'paragraph', paragraph: '...', size: '...', justify: '...' },
        { type: 'quote', text: '...' },
        { type: 'title', title: '...' },
        { type: 'divider' },
        { type: 'space' }
    ];

    const generatePrompt = () => {
        const promptText = `Generate a \"slices\" array for a website. Each slice must have a \"type\" and include all the necessary properties as defined in the following schema:\n\n${JSON.stringify(fullSliceSchema, null, 2)}\n\nNow, generate a random \"slices\" array containing all possible types with valid values for every property. pour les images, utilise le placeholder https://ik.imagekit.io/3txq1tb44/default-image.jpg?updatedAt=1746172781692`;
        setPrompt(promptText);
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveSuccess(false);
        setSaveError('');

        try {
            const parsedArray = JSON.parse(editableJson);

            if (!Array.isArray(parsedArray)) {
                throw new Error('Le contenu doit être un tableau JSON (array of slices).');
            }

            const pageData = {
                slices: parsedArray
            };

            const response = await fetch('/api/github/update-dictionaries', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ locale, pageKey, pageData })
            });

            const result = await response.json();
            if (response.ok) {
                addDeploymentToast('Déploiement du header en cours. Optimisation des fichiers...');
                setSaveSuccess(true);
                close();
            } else {
                setSaveError(result.error || 'Erreur lors de l’enregistrement');
            }
        } catch (error: any) {
            console.error('Erreur réseau ou JSON :', error);
            setSaveError(error.message || 'Erreur de réseau');
        } finally {
            setSaving(false);
        }
    };


    return (
        <>
            <Button
                onClick={() => {
                    generatePrompt();
                    open();
                }}
                color='black'
                style={{ position: 'fixed', top: 120, left: 20, zIndex: 1000 }}
            >
                Modifier le code de la page
            </Button>

            <Modal opened={opened} onClose={close} title="Page JSON & AI Prompt" size="xl">
                <Stack>
                    <Textarea
                        label="Page JSON"
                        value={editableJson}
                        onChange={(event) => setEditableJson(event.currentTarget.value)}
                        autosize={false}
                        minRows={10}
                        classNames={{ input: styles.input }}
                    />

                    <CopyButton value={prompt}>
                        {({ copied, copy }) => (
                            <Tooltip label={copied ? 'Copied!' : 'Copy prompt'} withArrow>
                                <Button onClick={copy} color={copied ? 'teal' : 'blue'}>
                                    {copied ? '✅ Copied' : '📋 Copy prompt'}
                                </Button>
                            </Tooltip>
                        )}
                    </CopyButton>

                    <Button onClick={handleSave} loading={saving} color="green">
                        💾 Enregistrer sur GitHub
                    </Button>

                    {saveSuccess && <div style={{ color: 'green' }}>✅ Sauvegarde réussie !</div>}
                    {saveError && <div style={{ color: 'red' }}>❌ {saveError}</div>}
                </Stack>
            </Modal>
        </>
    );
};
